import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, Phone, MapPin, Check } from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Logo } from "@/components/Logo";
import { CONTACT, MAP_GMAPS } from "@/lib/siteData";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

export function PillarHeader({ navigate, goTest }) {
  return (
    <header className="sticky top-0 z-40 bg-[#fafafa]/90 backdrop-blur-md border-b border-[#e8ebf2]">
      <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
        <button onClick={() => navigate("/")} data-testid="pillar-logo-home">
          <Logo imgClassName="h-12 sm:h-14" />
        </button>
        <button data-testid="pillar-nav-test" onClick={goTest}
          className="inline-flex items-center gap-2 bg-[#1a2d56] text-white font-medium rounded-full px-5 py-2.5 text-sm hover:bg-[#111e3a] transition-colors">
          Faire le test <ArrowRight size={16} />
        </button>
      </div>
    </header>
  );
}

export function PillarHero({ p, navigate, goTest }) {
  return (
    <section className="relative overflow-hidden bg-[#1a2d56]">
      <div className="absolute inset-0">
        <img src={p.image} alt={p.h1} className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a2d56] via-[#1a2d56]/90 to-[#1a2d56]/60" />
      </div>
      <div className="relative max-w-5xl mx-auto px-6 py-16 md:py-24">
        <nav className="flex items-center gap-2 text-xs text-white/60 mb-6" aria-label="Fil d'Ariane">
          <button onClick={() => navigate("/")} className="hover:text-white">Accueil</button>
          <ChevronRight size={14} />
          <span className="text-white/80">{p.eyebrow}</span>
        </nav>
        <span className="text-sm uppercase tracking-[0.2em] font-semibold text-[#d7de3b]">{p.eyebrow}</span>
        <h1 className="font-head font-light text-white leading-[1.05] tracking-tight text-4xl sm:text-5xl mt-4 max-w-3xl">
          {p.h1}
        </h1>
        <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-2xl">{p.intro}</p>
        <button data-testid="pillar-hero-test" onClick={goTest}
          className="mt-9 group inline-flex items-center gap-2 bg-[#d7de3b] text-[#1a2d56] font-semibold rounded-full px-8 py-4 hover:bg-white transition-colors text-sm">
          Je fais le test Restart gratuit <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </section>
  );
}

export function PillarArticle({ sections }) {
  return (
    <article className="space-y-12">
      {sections.map((s, i) => (
        <motion.section key={s.h} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}>
          <h2 className="font-head text-2xl sm:text-3xl font-medium text-[#1a2d56] tracking-tight">{s.h}</h2>
          <div className="mt-4 space-y-3">
            {s.p.map((para, j) =>
              para.startsWith("• ") ? (
                <p key={`${s.h}-${j}`} className="flex items-start gap-2.5 leading-relaxed">
                  <Check size={18} className="text-[#d7de3b] shrink-0 mt-1" />
                  <span>{para.slice(2)}</span>
                </p>
              ) : (
                <p key={`${s.h}-${j}`} className="leading-relaxed text-[#4b5b7c]">{para}</p>
              )
            )}
          </div>
        </motion.section>
      ))}
    </article>
  );
}

export function PillarFaq({ faqs }) {
  return (
    <section className="mt-16">
      <h2 className="font-head text-2xl sm:text-3xl font-medium text-[#1a2d56] tracking-tight">Questions fréquentes</h2>
      <Accordion type="single" collapsible className="mt-6" data-testid="pillar-faq">
        {faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-[#e8ebf2]">
            <AccordionTrigger className="text-left font-head text-[#1a2d56] hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-[#4b5b7c] leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export function PillarCta({ goTest }) {
  return (
    <section className="mt-16 bg-[#d7de3b] rounded-3xl px-8 py-12 text-center">
      <h2 className="font-head text-2xl sm:text-3xl font-medium text-[#1a2d56] tracking-tight">Prêt(e) à faire votre Restart ?</h2>
      <p className="mt-3 text-[#1a2d56]/80 max-w-xl mx-auto">Découvrez votre profil santé en 3 minutes et recevez vos conseils personnalisés. Gratuit, sans engagement.</p>
      <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
        <button data-testid="pillar-cta-test" onClick={goTest}
          className="inline-flex items-center justify-center gap-2 bg-[#1a2d56] text-white font-semibold rounded-full px-8 py-4 hover:bg-[#111e3a] transition-colors text-sm">
          Je fais le test <ArrowRight size={18} />
        </button>
        <a data-testid="pillar-cta-phone" href={`tel:${CONTACT.phoneRaw}`}
          className="inline-flex items-center justify-center gap-2 bg-white text-[#1a2d56] font-semibold rounded-full px-8 py-4 hover:shadow-lg transition-all text-sm">
          <Phone size={18} /> {CONTACT.phone}
        </a>
      </div>
    </section>
  );
}

export function PillarRelated({ others, navigate }) {
  return (
    <section className="mt-16">
      <h2 className="font-head text-xl font-medium text-[#1a2d56]">À lire aussi</h2>
      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        {others.map((o) => (
          <button key={o.slug} data-testid={`pillar-related-${o.slug}`} onClick={() => navigate(`/${o.slug}`)}
            className="text-left bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(26,45,86,0.08)] transition-all duration-300">
            <span className="text-xs uppercase tracking-wide font-semibold text-[#9aa6c0]">{o.eyebrow}</span>
            <p className="font-head text-lg font-medium text-[#1a2d56] mt-2 flex items-center gap-1">
              {o.h1} <ChevronRight size={16} className="text-[#d7de3b]" />
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}

export function PillarFooter() {
  return (
    <footer className="bg-[#1a2d56] text-white/80 py-12">
      <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-start justify-between gap-6">
        <div>
          <Logo imgClassName="h-11" />
          <p className="mt-3 text-sm text-white/60 max-w-xs">B.health® — Centre de santé préventive · Berchem-Sainte-Agathe, Bruxelles</p>
        </div>
        <ul className="space-y-2 text-sm">
          <li className="flex items-start gap-2.5"><MapPin size={16} className="text-[#d7de3b] shrink-0 mt-0.5" /> {CONTACT.address}</li>
          <li><a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-2.5 hover:text-white"><Phone size={16} className="text-[#d7de3b]" /> {CONTACT.phone}</a></li>
          <li><a href={MAP_GMAPS} target="_blank" rel="noopener noreferrer" className="hover:text-white underline">Itinéraire Google Maps</a></li>
        </ul>
      </div>
    </footer>
  );
}
